import { ApplicationConfig, ErrorHandler } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { bfHeaderInterceptor } from './interceptors/bf-header.interceptor';
import { localTracingInterceptorFn } from './interceptors/local-tracing.interceptor';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { environment } from 'src/environments/environment';
import { provideSentinelService, withLoggerConfig, withNewRelic } from '@bannerflow/sentinel';
import { NewRelicPlugin } from 'src/plugins/newrelic';
import { provideNotificationService } from '@bannerflow/notification';
import { BehaviorSubject } from 'rxjs';
import { provideFeatureFlags } from '@bannerflow/feature-flags';
import { GlobalErrorHandler } from 'src/plugins/global-error-handler.service';
import { providePREnvPicker } from '@bannerflow/ui';
import { ACCOUNT_ACCESS_URL } from '@bannerflow/intercom-messenger';

export const appConfig: ApplicationConfig = {
    providers: [
        provideSentinelService(
            withNewRelic({
                enabled: environment.stage !== 'local' && environment.stage !== 'proxy',
                ...NewRelicPlugin.init(environment.stage === 'production', [...Object.values(environment.origins)])
            }),
            withLoggerConfig({
                level: 'debug',
                production: environment.stage === 'production',
                sandbox: environment.stage === 'sandbox',
                stage: environment.stage
            })
        ),
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        {
            provide: ACCOUNT_ACCESS_URL,
            useValue: environment.origins.accountAccessService
        },
        provideAnimations(),
        provideRouter(routes),
        provideHttpClient(
            withInterceptors([
                bfHeaderInterceptor,
                authHttpInterceptorFn,
                // In non-local environments, distributed tracing is handled by the Sentinel package.
                ...(environment.stage === 'local' ? [localTracingInterceptorFn] : [])
            ])
        ),
        provideNotificationService({
            apiUri: environment.origins.notificationServiceApi,
            accessToken$: new BehaviorSubject<string | undefined>(undefined),
            signalR: {
                enabled: true,
                url: environment.origins.notificationServiceHub
            }
        }),
        provideAuth0({
            clientId: environment.auth.clientId,
            domain: environment.auth.domain,
            authorizationParams: {
                redirect_uri: document.baseURI.replace(/\/$/, ''),
                audience: environment.auth.audience,
                scope: 'email listservice feedssupremecommander feedsdeliverymaster feedssocializingmaster feedsfearsomeprocessor studio bannerflow profile socialaccountservice design'
            },
            httpInterceptor: {
                allowedList: Object.values(environment.origins).map(origin => ({
                    uri: `${origin}/*`,
                    tokenOptions: {
                        authorizationParams: {
                            audience: environment.auth.audience
                        }
                    }
                }))
            },
            cacheLocation: 'localstorage',
            useRefreshTokens: true
        }),
        provideFeatureFlags({
            enabled: true,
            appName: 'feeds-client',
            url: environment.origins.featureFlagService,
            clientKey: environment.featureFlags.clientKey,
            lazyStart: true
        }),
        providePREnvPicker({
            beRepos: [
                { path: 'feeds-fearsome-processor', repoName: 'feeds-fearsome-processor' },
                { path: 'feeds-supreme-commander', repoName: 'feeds-supreme-commander' }
            ],
            feRepos: [{ repoName: 'feeds-client', skipRedirection: true }]
        })
    ]
};
